import React, { useMemo, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import StatusBadge from '@/dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import { WEBHOOK_STATUS } from '@dashboard/enums/webhook-events';
import WebhookDeleteDialog from './WebhookDeleteDialog';
import WebhookEditDialog from './WebhookEditDialog';
import WebhookActionsMenu from './WebhookActionsMenu';
import { Badge } from '@/dashboard/components/ui/badge';

const formatEvents = events => {
  if (!events || events.length === 0) return 'None';
  if (events.length === 1)
    return (
      <Tooltip label={events.join(', ')}>
        <Badge variant="secondary" className="first-letter:uppercase">{`${events[0]}`}</Badge>
      </Tooltip>
    );

  return (
    <Tooltip label={events.join(', ')}>
      <Badge
        variant="secondary"
        className="first-letter:uppercase"
      >{`${events[0]} +${events.length - 1} more`}</Badge>
    </Tooltip>
  );
};

const formatSuccessRate = webhook => {
  if (!webhook || webhook.deliveryCount === 0) return 'N/A';
  const successRate = (webhook.successCount / webhook.deliveryCount) * 100;
  return `${Math.round(successRate)}%`;
};

const getStatusAppearance = status => {
  switch (status) {
    case WEBHOOK_STATUS.ACTIVE:
      return 'success';
    case WEBHOOK_STATUS.INACTIVE:
      return 'warning';
    case WEBHOOK_STATUS.FAILED:
      return 'error';
    default:
      return 'neutral';
  }
};

const formatLastActivity = webhook => {
  if (webhook.lastSuccessAt) {
    return formatDateTime(webhook.lastSuccessAt);
  }
  if (webhook.lastTriggeredAt) {
    return `Failed: ${formatDateTime(webhook.lastTriggeredAt)}`;
  }
  return 'Never';
};

const WebhookTable = ({ data, isLoading, isError }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);

  const handleDeleteClick = webhook => {
    setSelectedWebhook(webhook);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = webhook => {
    setSelectedWebhook(webhook);
    setEditDialogOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const webhook = row.original;
          return (
            <div>
              <div className="font-medium">{webhook.name}</div>
              <div className="text-xs text-gray-500 dark:text-muted-foreground">
                Created {formatDateTime(webhook.createdAt)}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <Tooltip label={row.original.url}>
            <code className="text-xs bg-gray-100 dark:bg-muted px-2 py-1">{row.original.url}</code>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'events',
        header: 'Events',
        cell: ({ row }) => formatEvents(row.original.events),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge
            appearance={getStatusAppearance(row.original.status)}
            label={row.original.status}
          />
        ),
      },
      {
        id: 'successRate',
        header: 'Success rate',
        cell: ({ row }) => formatSuccessRate(row.original),
        meta: { className: 'text-center' },
      },
      {
        id: 'lastActivity',
        header: 'Last activity',
        cell: ({ row }) => <div className="text-sm">{formatLastActivity(row.original)}</div>,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <WebhookActionsMenu
            webhook={row.original}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <Alert variant="destructive" className="my-4">
        <ExclamationCircleIcon />
        <AlertDescription>Failed to load webhooks</AlertDescription>
      </Alert>
    );
  }

  const webhooks = data?.webhooks ?? [];

  const emptyState = (
    <EmptyState>
      <EmptyState.Title>No webhooks yet</EmptyState.Title>
      <EmptyState.Subtitle>
        Create your first webhook to receive real-time notifications.
      </EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <>
      <DataGrid columns={columns} data={webhooks} isLoading={isLoading} emptyState={emptyState} />

      <WebhookDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
      />

      <WebhookEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
      />
    </>
  );
};

export default WebhookTable;
